import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/state/useAuthStore";
import type { MicrositeUser } from "@/types/api";

/**
 * Microsite API client.
 *
 * - In dev, requests go to `/api` proxied to localhost:3001 (see vite.config.ts).
 * - In staging/prod, set VITE_API_BASE_URL to the canonical API origin.
 *
 * `withCredentials: true` is required so the httpOnly `microsite_rt` cookie
 * is sent on /auth/refresh.
 *
 * The interceptor auto-refreshes the access token on 401 once per request.
 */
const baseURL = import.meta.env.VITE_API_BASE_URL || "/api";

export const api = axios.create({
  baseURL,
  withCredentials: true
});

// Public client — no auth headers, no auto-refresh. Used for /auth/login,
// /auth/signup, and reference endpoints called before login.
export const publicApi = axios.create({
  baseURL,
  withCredentials: true
});

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers = config.headers || {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const { data } = await publicApi.post<{
        accessToken: string;
        user: MicrositeUser;
      }>("/microsite/auth/refresh");
      useAuthStore.getState().setAuth(data.accessToken, data.user);
      return data.accessToken;
    } catch {
      useAuthStore.getState().clearAuth();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    if (!config || config._retry) return Promise.reject(error);

    const status = error.response?.status;
    // Only attempt refresh on 401 from non-auth endpoints. Auth endpoints
    // returning 401 are genuine login failures.
    const url = config.url || "";
    const isAuthEndpoint = url.includes("/microsite/auth/login") || url.includes("/microsite/auth/refresh");
    if (status !== 401 || isAuthEndpoint) return Promise.reject(error);

    config._retry = true;
    const newToken = await refreshAccessToken();
    if (!newToken) return Promise.reject(error);

    config.headers = config.headers || {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
    return api.request(config);
  }
);

/** Try to restore an authenticated session on app boot. */
export async function bootstrapAuth(): Promise<void> {
  useAuthStore.getState().setBootStatus("loading");
  try {
    await refreshAccessToken();
  } finally {
    useAuthStore.getState().setBootStatus("ready");
  }
}
